/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-unused-vars */
/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { create, type UseBoundStore } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

// ---- Types ----
export interface SkuAttribute {
  sku: string;
  sellingPrice: string;
}

type UIState = {
  attribute: SkuAttribute[] | null;
  skuSelected: string | null;

  // actions
  reset: () => void;
  setAttribute: (attr: SkuAttribute[] | null) => void;
  patchAttribute: (patch: SkuAttribute[]) => void;
  updateSku: (sku: string | null) => void;
};

// ---- create store ----
function createUIStore(): UseBoundStore<UIState> {
  return create<UIState>()(
    subscribeWithSelector((set, get) => ({
      attribute: null,
      skuSelected: null,

      reset: () =>
        set({
          attribute: null,
          skuSelected: null,
        }),

      setAttribute: (attr) => {
        set({ attribute: attr });
      },
      updateSku: (sku) => {
        set({ skuSelected: sku });
      },

      patchAttribute: (patch) =>
        set((state) => ({
          attribute: state.attribute ? [...state.attribute, ...patch] : patch,
        })),
    })),
  );
}

// ---- Singleton store ----
declare global {
  interface Window {
    __INSURANCE_STORE_API__?: {
      get: () => { attribute: SkuAttribute[] | null; skuSelected: string | null };
      unmount?: () => void;
    };
  }
}

let storeInstance: UseBoundStore<UIState> | null = null;
export const useUIStore: UseBoundStore<UIState> = (() => {
  if (!storeInstance) storeInstance = createUIStore();
  return storeInstance;
})();

// ---- Freeze state to prevent console mutation (only when exposing via window API) ----
function deepFreeze<T>(obj: T): T {
  const seen = new WeakSet();
  function freeze(o: any) {
    if (o === null || typeof o !== 'object' || seen.has(o)) return;
    seen.add(o);
    // eslint-disable-next-line security/detect-object-injection
    Object.keys(o).forEach((k) => freeze(o[k]));
    try {
      Object.freeze(o);
    } catch (error) {
      console.warn('Failed to freeze object in store:', error);
    }
  }
  freeze(obj);
  return obj;
}

// ---- EVENT SYSTEM (for cross-repo communication) ----
// Allows external code to manipulate store values via window events

// Payload validation helpers
const validateSetAttribute = (payload: unknown): SkuAttribute[] | null => {
  if (payload === null || payload === undefined) return null;
  if (Array.isArray(payload)) {
    return payload.filter(
      (item): item is SkuAttribute =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as any).sku === 'string' &&
        typeof (item as any).sellingPrice === 'string',
    );
  }
  throw new Error('Invalid payload for setAttribute: expected array of SkuAttribute or null');
};

const validateUpdateSku = (payload: unknown): string | null => {
  if (payload === null || payload === undefined) return null;
  if (typeof payload === 'string') return payload;
  throw new Error('Invalid payload for updateSku: expected string or null');
};

let cleanupEventListeners: (() => void) | null = null;

if (typeof window !== 'undefined') {
  const ACTION_EVENT = 'product-fe:store-action';
  const UPDATED_EVENT = 'product-fe:store-updated';

  const handleActionPayload = (detail: any) => {
    const { type, payload } = detail ?? {};
    if (!type || typeof type !== 'string') {
      console.warn('Invalid action type in store event:', type);
      return;
    }

    try {
      switch (type) {
        case 'setAttribute':
          useUIStore.getState().setAttribute(validateSetAttribute(payload));
          break;
        case 'patchAttribute':
          if (Array.isArray(payload)) {
            const validated = payload.filter(
              (item): item is SkuAttribute =>
                typeof item === 'object' &&
                item !== null &&
                typeof (item as any).sku === 'string' &&
                typeof (item as any).sellingPrice === 'string',
            );
            useUIStore.getState().patchAttribute(validated);
          } else {
            console.warn('Invalid payload for patchAttribute: expected array');
          }
          break;
        case 'updateSku':
          useUIStore.getState().updateSku(validateUpdateSku(payload));
          break;
        case 'reset':
          useUIStore.getState().reset();
          break;
        default:
          console.warn(`Unknown action type: ${type}`);
      }
    } catch (err) {
      console.warn('Failed to handle store action:', err);
    }
  };

  // Listen for action events
  // Usage: window.dispatchEvent(new CustomEvent('product-fe:store-action', {
  //   detail: { type: 'setAttribute', payload: [{ sku: 'ABC123', sellingPrice: '99.99' }] }
  // }));
  const actionHandler = (e: Event) => {
    const ev = e as CustomEvent;
    handleActionPayload(ev.detail);
  };

  window.addEventListener(ACTION_EVENT, actionHandler);

  // Emit updated events when store changes
  let previous = useUIStore.getState();
  const unsubscribe = useUIStore.subscribe((next) => {
    const changedKeys: string[] = [];
    Object.keys(next).forEach((k) => {
      // eslint-disable-next-line security/detect-object-injection
      if ((next as any)[k] !== (previous as any)[k]) changedKeys.push(k);
    });
    previous = next as any;

    window.dispatchEvent(
      new CustomEvent(UPDATED_EVENT, {
        detail: { changedKeys, state: next },
      }),
    );
  });

  // Store cleanup function
  cleanupEventListeners = () => {
    window.removeEventListener(ACTION_EVENT, actionHandler);
    unsubscribe();
  };
}

// Export cleanup for testing/development
export const cleanupStore = () => {
  if (cleanupEventListeners) {
    cleanupEventListeners();
    cleanupEventListeners = null;
  }
};

// ---- Safe public API (read-only) ----

function safeClone<T>(v: T): T {
  if (typeof (globalThis as any).structuredClone === 'function') {
    try {
      return (globalThis as any).structuredClone(v);
    } catch (error) {
      console.warn('Failed to use structuredClone, falling back to JSON:', error);
    }
  }
  return JSON.parse(JSON.stringify(v));
}

function getSerializableState() {
  const { attribute, skuSelected } = useUIStore.getState();
  const state = safeClone({ attribute, skuSelected });
  // Freeze only when exposing via window API to prevent external mutation
  return deepFreeze(state);
}

if (typeof window !== 'undefined') {
  if (!window.__INSURANCE_STORE_API__) {
    const api = {
      get: () => getSerializableState(),
      unmount: () => {
        cleanupStore();
        try {
          delete window.__INSURANCE_STORE_API__;
        } catch (error) {
          console.warn('Failed to delete window.__INSURANCE_STORE_API__:', error);
        }
      },
    };

    Object.defineProperty(window, '__INSURANCE_STORE_API__', {
      configurable: true,
      enumerable: false,
      writable: false,
      value: Object.freeze(api),
    });
  }
}
